/**
 * @fileoverview グループ化された出典カードコンポーネント
 * @description 同じドキュメント+ページのソースを1つのカードにまとめて表示
 * @remarks
 * - アコーディオン展開で全文表示（初期状態は100文字）
 * - 拡張クエリのキーワードでハイライト表示
 * - タイトルクリックで配布元PDFを別タブで開く
 */

import { useMemo, useState } from 'react'
import { ChevronDown, ChevronUp, ExternalLink } from 'lucide-react'
import type { ChatSource } from '@karosorabei/shared'

import { cn } from '@/lib/utils'
import { groupSourcesByPage, type GroupedSource } from '@/lib/source-grouping'
import { highlightText, type HighlightSegment } from '@/lib/highlight'
import { usePdfViewer, isMobileDevice } from '@/components/pdf/pdf-viewer-context'

/**
 * GroupedSourceListのprops型
 */
export interface GroupedSourceListProps {
  /** ソース情報の配列 */
  sources: ChatSource[]
  /** ハイライトするキーワード配列（クエリ拡張で生成） */
  expandedKeywords?: string[]
  /** カスタムクラス名 */
  className?: string
}

/**
 * グループ化されたソース一覧コンポーネント
 * @description 同じドキュメント+ページのソースを1つのカードにまとめて表示
 * スコア上位3つの引用のみを表示（グループ3つではなく引用3つ）
 *
 * @example
 * ```tsx
 * <GroupedSourceList
 *   sources={chatResponse.sources}
 *   expandedKeywords={chatResponse.expandedKeywords}
 * />
 * ```
 */
export function GroupedSourceList({
  sources,
  expandedKeywords = [],
  className,
}: GroupedSourceListProps) {
  // ソースをグループ化し、上位3引用のみを含むグループを作成（メモ化）
  const filteredGroups = useMemo(() => {
    const groupedSources = groupSourcesByPage(sources)

    // 全引用をフラット化してスコア順でソート（各引用ごとの階層情報を保持）
    const allQuotes = groupedSources.flatMap((group) =>
      group.quotes.map((quote) => ({
        ...quote,
        groupKey: group.key,
        documentTitle: group.documentTitle,
        // 階層情報は各引用が持つものを優先（なければグループのものを使用）
        chapterText: quote.chapterText ?? group.chapterText,
        sectionText: quote.sectionText ?? group.sectionText,
        subsectionText: quote.subsectionText ?? group.subsectionText,
        page: group.page,
        actualPdfPage: group.actualPdfPage,
        sourceUrl: group.sourceUrl,
      }))
    )
    allQuotes.sort((a, b) => b.score - a.score)

    // 上位3引用のみを取得
    const topQuotes = allQuotes.slice(0, 3)

    // 上位3引用をグループ化し直す（各引用に階層情報を保持）
    const filteredGroupMap = new Map<string, GroupedSource>()
    for (const quote of topQuotes) {
      const existingGroup = filteredGroupMap.get(quote.groupKey)
      if (existingGroup) {
        // 各引用ごとに階層情報を保持
        const newQuote: GroupedSource['quotes'][0] = {
          chunkId: quote.chunkId,
          text: quote.text,
          score: quote.score,
        }
        if (quote.chapterText !== undefined) {
          newQuote.chapterText = quote.chapterText
        }
        if (quote.sectionText !== undefined) {
          newQuote.sectionText = quote.sectionText
        }
        if (quote.subsectionText !== undefined) {
          newQuote.subsectionText = quote.subsectionText
        }
        existingGroup.quotes.push(newQuote)
      } else {
        // 新しいグループを作成（最初の引用にも階層情報を保持）
        const firstQuote: GroupedSource['quotes'][0] = {
          chunkId: quote.chunkId,
          text: quote.text,
          score: quote.score,
        }
        if (quote.chapterText !== undefined) {
          firstQuote.chapterText = quote.chapterText
        }
        if (quote.sectionText !== undefined) {
          firstQuote.sectionText = quote.sectionText
        }
        if (quote.subsectionText !== undefined) {
          firstQuote.subsectionText = quote.subsectionText
        }

        const newGroup: GroupedSource = {
          key: quote.groupKey,
          documentTitle: quote.documentTitle,
          maxScore: quote.score,
          quotes: [firstQuote],
        }
        if (quote.page !== undefined) {
          newGroup.page = quote.page
        }
        // グループレベルの階層情報は不要になるが、互換性のために保持
        if (quote.chapterText !== undefined) {
          newGroup.chapterText = quote.chapterText
        }
        if (quote.sectionText !== undefined) {
          newGroup.sectionText = quote.sectionText
        }
        if (quote.subsectionText !== undefined) {
          newGroup.subsectionText = quote.subsectionText
        }
        if (quote.sourceUrl !== undefined) {
          newGroup.sourceUrl = quote.sourceUrl
        }
        if (quote.actualPdfPage !== undefined) {
          newGroup.actualPdfPage = quote.actualPdfPage
        }
        filteredGroupMap.set(quote.groupKey, newGroup)
      }
    }

    // グループを最高スコア順でソート
    const result = Array.from(filteredGroupMap.values())
    result.sort((a, b) => b.maxScore - a.maxScore)
    return result
  }, [sources])

  if (filteredGroups.length === 0) {
    return null
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {/* 検索キーワード表示（expandedKeywordsがある場合のみ） */}
      {expandedKeywords.length > 0 && (
        <div className="px-2 py-1.5 rounded bg-blue-50 border border-blue-100">
          <span className="text-[10px] text-blue-600 font-medium">検索キーワード: </span>
          <span className="text-[10px] text-blue-800">{expandedKeywords.join(', ')}</span>
        </div>
      )}
      {filteredGroups.map((group) => (
        <GroupedSourceCard key={group.key} group={group} expandedKeywords={expandedKeywords} />
      ))}
    </div>
  )
}

/**
 * GroupedSourceCardのprops型
 */
interface GroupedSourceCardProps {
  /** グループ化された出典情報 */
  group: GroupedSource
  /** ハイライトするキーワード配列 */
  expandedKeywords: string[]
}

/**
 * グループ化された出典カードコンポーネント
 * @description 1つのドキュメント+ページからの複数の引用を表示
 * @remarks
 * - デスクトップ: タイトルクリックで配布元PDFを別タブで開く（sourceUrl#page=Nの形式）
 * - モバイル: PDFビューアダイアログで表示（Android ChromeでのPDFページ指定非対応対策）
 */
function GroupedSourceCard({ group, expandedKeywords }: GroupedSourceCardProps) {
  // PDFビューアContextを取得
  const { openPdfViewer } = usePdfViewer()

  // 複数の引用がある場合のみグループ表示
  const hasMultipleQuotes = group.quotes.length > 1

  // ページ番号を表示用に構築（文書ページ番号をそのまま表示）
  const pageText = group.page ? `p.${group.page}` : ''

  // PDF物理ページ番号（APIから直接渡される）
  // @remarks actualPdfPageがある場合はそれを使用、なければpageをフォールバック（後方互換性）
  const pdfPhysicalPage = group.actualPdfPage ?? group.page

  // PDF URLを構築（物理ページ番号を使用）
  const pdfUrl = group.sourceUrl
    ? pdfPhysicalPage
      ? `${group.sourceUrl}#page=${pdfPhysicalPage}`
      : group.sourceUrl
    : undefined

  /**
   * タイトルクリックでPDFを開く
   * @remarks
   * - デスクトップ: 従来どおり別タブで開く（#page=N対応、物理ページ番号を使用）
   * - モバイル: PDFビューアダイアログで表示（Android Chrome対策、物理ページ番号を使用）
   */
  const handleTitleClick = () => {
    if (!group.sourceUrl) return

    const isMobile = isMobileDevice()

    if (isMobile && pdfPhysicalPage) {
      // モバイルの場合はPDFビューアダイアログで表示（物理ページ番号を渡す）
      openPdfViewer(group.sourceUrl, pdfPhysicalPage)
    } else if (pdfUrl) {
      // デスクトップの場合は従来どおり別タブで開く
      window.open(pdfUrl, '_blank', 'noopener,noreferrer')
    }
  }

  return (
    <div className="rounded-lg bg-[#F5F4F1] overflow-hidden">
      {/* カードヘッダー：ドキュメント情報とページ番号のみ（階層パスは各引用に移動） */}
      <div className="px-3 py-2 border-b border-[#E5E4E1]">
        <div className="flex items-start gap-2">
          <ExternalLink className="h-3.5 w-3.5 flex-shrink-0 text-[#1565C0] mt-0.5" />
          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
            {/* タイトルをクリック可能に（sourceUrlがある場合のみ） */}
            {pdfUrl ? (
              <button
                type="button"
                onClick={handleTitleClick}
                className="text-xs font-medium text-[#1565C0] hover:underline text-left cursor-pointer"
              >
                {group.documentTitle}
              </button>
            ) : (
              <span className="text-xs font-medium text-[#1565C0]">{group.documentTitle}</span>
            )}
            {/* ページ番号のみ表示 */}
            {pageText && <span className="text-[10px] text-[#9C9B99]">{pageText}</span>}
          </div>
        </div>
      </div>

      {/* カードボディ：引用リスト */}
      <div className="px-3 py-2 flex flex-col gap-2">
        {group.quotes.map((quote, index) => (
          <QuoteItem
            key={quote.chunkId}
            quote={quote}
            index={index + 1}
            showLabel={hasMultipleQuotes}
            expandedKeywords={expandedKeywords}
          />
        ))}
      </div>
    </div>
  )
}

/**
 * QuoteItemのprops型
 */
interface QuoteItemProps {
  /** 引用情報（階層情報を含む） */
  quote: {
    chunkId: string
    text: string
    score: number
    /** 章テキスト */
    chapterText?: string
    /** 節テキスト */
    sectionText?: string
    /** 項テキスト */
    subsectionText?: string
  }
  /** 引用番号（1から始まる） */
  index: number
  /** 「引用 n」ラベルを表示するか */
  showLabel: boolean
  /** ハイライトするキーワード配列 */
  expandedKeywords: string[]
}

/** 初期表示時の最大文字数 */
const INITIAL_TEXT_LENGTH = 100

/**
 * 引用アイテムコンポーネント
 * @description 1つの引用テキストとスコアを表示
 * @remarks アコーディオン展開で全文表示、キーワードハイライト付き、階層パス表示
 */
function QuoteItem({ quote, index, showLabel, expandedKeywords }: QuoteItemProps) {
  const [isExpanded, setIsExpanded] = useState(false)

  // 全文表示が必要かどうか（100文字を超える場合のみアコーディオン表示）
  const needsExpansion = quote.text.length > INITIAL_TEXT_LENGTH

  // 表示するテキスト（展開状態に応じて切り替え）
  const displayText = isExpanded ? quote.text : quote.text.slice(0, INITIAL_TEXT_LENGTH)

  // ハイライト処理（メモ化）
  const highlightedSegments = useMemo(() => {
    return highlightText(displayText, expandedKeywords)
  }, [displayText, expandedKeywords])

  // 階層パスを構築（章 > 節 > 項）- 各引用ごとに表示
  const hierarchyParts: string[] = []
  if (quote.chapterText) {
    hierarchyParts.push(quote.chapterText)
  }
  if (quote.sectionText) {
    hierarchyParts.push(quote.sectionText)
  }
  if (quote.subsectionText) {
    hierarchyParts.push(quote.subsectionText)
  }
  const hierarchyPath = hierarchyParts.join(' > ')

  /**
   * アコーディオン開閉を切り替え
   */
  const handleToggle = () => {
    setIsExpanded((prev) => !prev)
  }

  return (
    <div className="flex flex-col gap-1 rounded bg-white/50 px-2.5 py-2">
      {/* 引用ラベルとスコア */}
      <div className="flex items-center justify-between">
        {showLabel ? (
          <span className="text-[10px] font-medium text-[#9E9E9E]">引用 {index}</span>
        ) : (
          <span />
        )}
        <DebugScoreBadge score={quote.score} />
      </div>

      {/* 階層パス（章 > 節 > 項）- 各引用ごとに表示 */}
      {hierarchyPath && (
        <span className="text-[10px] text-[#9C9B99] leading-tight">{hierarchyPath}</span>
      )}

      {/* 引用テキスト（ハイライト付き） */}
      <p className="text-[11px] leading-relaxed text-[#1A1918]">
        「
        <HighlightedText segments={highlightedSegments} />
        {!isExpanded && needsExpansion && '...'}」
      </p>

      {/* アコーディオン展開ボタン（100文字を超える場合のみ） */}
      {needsExpansion && (
        <button
          type="button"
          onClick={handleToggle}
          className="flex items-center gap-1 text-[10px] text-[#1565C0] hover:underline self-start mt-0.5"
        >
          {isExpanded ? (
            <>
              <ChevronUp className="h-3 w-3" />
              閉じる
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3" />
              全文を表示
            </>
          )}
        </button>
      )}
    </div>
  )
}

/**
 * HighlightedTextのprops型
 */
interface HighlightedTextProps {
  /** ハイライトセグメントの配列 */
  segments: HighlightSegment[]
}

/**
 * ハイライト表示コンポーネント
 * @description セグメントに分割されたテキストをハイライト付きで表示
 */
function HighlightedText({ segments }: HighlightedTextProps) {
  return (
    <>
      {segments.map((segment, idx) =>
        segment.isHighlighted ? (
          <mark key={idx} className="bg-yellow-200 px-0.5 rounded">
            {segment.text}
          </mark>
        ) : (
          <span key={idx}>{segment.text}</span>
        )
      )}
    </>
  )
}

// ============================================================================
// TODO: デバッグ用コンポーネント - 本番リリース前に削除すること
// ============================================================================

/**
 * デバッグ用スコアバッジ
 * @description RAGのマッチ度をパーセンテージで表示
 * @remarks
 * TODO: 本番リリース前にこのコンポーネントと使用箇所を削除すること
 */
function DebugScoreBadge({ score }: { score: number }) {
  // スコアをパーセンテージに変換（0-1 → 0-100%）
  const percentage = Math.round(score * 100)

  // スコアに応じた色を設定
  const colorClass =
    percentage >= 80
      ? 'bg-green-100 text-green-700'
      : percentage >= 60
        ? 'bg-yellow-100 text-yellow-700'
        : 'bg-red-100 text-red-700'

  return (
    <span className={cn('text-[9px] px-1.5 py-0.5 rounded-full font-mono', colorClass)}>
      {percentage}%
    </span>
  )
}
